import { type ReactNode, cloneElement, useRef, useState } from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { HardHatIcon } from "@phosphor-icons/react/HardHat";
import { Icon } from "../../Icons/Icon";
import { Spinner } from "../../Feedback/Spinner";
import { Tooltip } from "../../Feedback/Tooltip";
import { headingFontStyle } from "../../Theme/headingFontStyle";
import { ConfirmDialog } from "../../Dialog/Internal/ConfirmDialog";
import {
  useDialogTrayHasEntry,
  useDialogTrayMutatorsOptional,
} from "../../Dialog/DialogTrayProvider";
import {
  toneIconColors,
  menuToneClass,
  menuItemClasses,
  disabledStyles,
} from "./buttonStyles";
import {
  useShowMenuDialog,
  useCloseMenu,
} from "../../Menu/Internal/MenuContext";
import { useMenuMode } from "../../Menu/MenuModeContext";
import { useMenuIconColumn } from "../../Menu/MenuIconColumnContext";
import { useInsideDialog } from "../../Dialog/Internal/InsideDialogContext";
import type { ButtonProps } from "../Button";

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function MenuItemButton_({
  tone = "neutral",
  icon: IconProp,
  iconWeight,
  children,
  onClick,
  disabled,
  disabledMessage,
  loading,
  dialog,
  tray,
  dedupeKey,
  confirm,
  href,
  wip,
  "data-testid": dataTestid,
}: ButtonProps) {
  const showDialog = useShowMenuDialog();
  const closeMenu = useCloseMenu();
  const menuMode = useMenuMode();
  // Mirror StandardButton_'s tray behavior: with provider in scope and
  // tray=true, route the dialog into the tray instead of the menu's
  // showDialog slot. Mutators-only subscription — see Button.tsx.
  const trayCtx = useDialogTrayMutatorsOptional();
  // Tray-by-default: mirrors `StandardButton_` in `Button.tsx`. Without
  // this, Button-as-menu-item bypasses the parent's tray-by-default
  // because `Button.tsx` returns early to `MenuItemButton_` before
  // reaching its own `useTray` line. Keep these two lines in lockstep —
  // see ENG-4836 for the parking-by-default decision.
  const useTray = !!(dialog && trayCtx);
  // Mirrors StandardButton_: a dialog opened from inside another dialog
  // parks its chip above the parent's overlay, or it'd be unreachable.
  const insideDialog = useInsideDialog();
  const warnedRef = useRef(false);
  // Mirror StandardButton_'s parked-blocking: when this item's dialog is
  // already parked in the tray, disable the item and explain why — otherwise
  // a menu reopen would let the operator open a duplicate. `activeTrayKey`
  // dies when the menu closes (item unmounts), so the cross-reopen guard is
  // `trayHasMatchingEntry`, which requires the caller to pass a `dedupeKey`.
  const [activeTrayKey, setActiveTrayKey] = useState<string | undefined>(
    undefined,
  );
  const trayHasMatchingEntry = useDialogTrayHasEntry(dedupeKey);
  const isParked = !!activeTrayKey || trayHasMatchingEntry;
  const effectiveDisabled = disabled || wip || isParked;
  const parkedTooltip = isParked
    ? "Already parked — click the chip on the right to resume."
    : undefined;
  const confirmConfig = confirm
    ? typeof confirm === "string"
      ? { title: confirm, message: confirm as ReactNode }
      : confirm
    : undefined;
  const itemClass = menuItemClasses;
  // Icon is the brand blue, flipping to white when the row is highlighted (the
  // blue selection). `group-data-[highlighted]` reads the item's Radix state.
  const neutralIconColor =
    tone === "neutral"
      ? "text-brand-text group-data-[highlighted]:text-white"
      : undefined;
  // Reserve icon column space so text aligns across all menu items — unless
  // the menu has declared that none of its rows carry an icon, in which case
  // the column is dead space and the labels sit flush instead.
  const reserveIconColumn = useMenuIconColumn();
  const iconElement = loading ? (
    <Spinner size={14} onColor />
  ) : IconProp ? (
    <Icon
      icon={IconProp}
      size="sm"
      {...(neutralIconColor ? { color: neutralIconColor } : {})}
      {...(iconWeight ? { weight: iconWeight } : {})}
    />
  ) : reserveIconColumn ? (
    <span className="w-3.5 shrink-0" />
  ) : undefined;
  const handleSelect = (e?: { preventDefault: () => void }) => {
    // No href branch: an href item renders a real anchor instead (see below).
    if (dialog) {
      if (tray && !trayCtx && !warnedRef.current) {
        console.warn(
          "Menu Button has tray={true} but no DialogTrayProvider in scope — falling back to local mounting.",
        );
        warnedRef.current = true;
      }
      if (useTray) {
        // Tray path: register the dialog, then close the menu ourselves.
        // Radix's own close-on-select loses the race against the tray
        // Dialog mounting and stealing focus in the same tick, so the menu
        // would otherwise linger (visible and clickable) on top of the
        // dialog. Mirror the non-tray showDialog path, which force-closes.
        // The tray host owns the dialog from here on, so no `showDialog(...)`
        // and no local cleanup — the dialog's onOpenChange roundtrips through
        // the tray.
        //
        // dedupeKey forwarding is the menu's whole answer to the "open
        // again from the same menu item" case: the menu closes on
        // select, the user reopens it, clicks the same item, and the
        // provider returns the existing handle (restoring if parked)
        // instead of registering a second dialog. No per-item state
        // needed here — the provider holds the truth.
        const handle = trayCtx.openElement(dialog(), {
          onClose: () => setActiveTrayKey(undefined),
          ...(dedupeKey ? { dedupeKey } : {}),
          ...(insideDialog ? { chipAboveModals: true } : {}),
        });
        setActiveTrayKey(handle.key);
        closeMenu();
      } else {
        // Use showDialog to close menu and render dialog as sibling (same pattern as confirm).
        // Z-index for nested dialogs is handled by the DialogHost open-stack registry.
        showDialog(
          cloneElement(dialog(), {
            open: true,
            onOpenChange: (open: boolean) => {
              if (!open) showDialog(undefined);
            },
          }),
        );
      }
      return;
    }
    if (confirm && confirmConfig) {
      e?.preventDefault();
      showDialog(
        <ConfirmDialog
          open
          onClose={() => showDialog(undefined)}
          onConfirm={async (input?: string) => {
            await onClick?.(input);
            showDialog(undefined);
          }}
          title={confirmConfig.title}
          message={confirmConfig.message}
          confirmLabel={confirmConfig.confirmLabel ?? "Confirm"}
          cancelLabel={confirmConfig.cancelLabel}
          confirmTone={tone}
          icon={confirmConfig.icon}
          iconColors={toneIconColors[confirmConfig.iconTone ?? tone]}
          inputLabel={confirmConfig.inputLabel}
          inputPlaceholder={confirmConfig.inputPlaceholder}
          inputDefaultValue={confirmConfig.inputDefaultValue}
        />,
      );
      return;
    }
    onClick?.();
    // Close explicitly rather than leaving it to Radix's close-on-select. A
    // hover-opened menu refuses Radix's close (see Menu's handleOpenChange), so
    // without this the menu stays up after the item runs — hanging over the
    // page and eating the next click.
    closeMenu();
  };
  const wipBadge = wip ? (
    <Icon icon={HardHatIcon} size="xs" weight="fill" color="text-yellow-500" />
  ) : undefined;
  const menuItem = (content: ReactNode) => {
    const tooltipContent =
      parkedTooltip ??
      (effectiveDisabled && disabledMessage ? disabledMessage : undefined);
    if (tooltipContent) {
      // block: menu items are block-level rows — the default inline-flex
      // trigger makes two adjacent wrapped items share a single menu row.
      return (
        <Tooltip content={tooltipContent} block>
          {content}
        </Tooltip>
      );
    }
    return content;
  };

  if (menuMode === "inline") {
    return menuItem(
      <div
        role="menuitem"
        {...(dataTestid !== undefined && { "data-testid": dataTestid })}
        className={`${itemClass} ${menuToneClass[tone]} ${effectiveDisabled ? disabledStyles : ""}`}
        style={headingFontStyle()}
      >
        {iconElement}
        {children}
        {wipBadge}
      </div>,
    );
  }
  const itemIsDisabled = effectiveDisabled === true || loading === true;
  const itemClassName = `${itemClass} ${menuToneClass[tone]} ${effectiveDisabled ? disabledStyles : ""}`;
  const itemBody = (
    <>
      {iconElement}
      {children}
      {wipBadge}
    </>
  );

  // An href item renders a real <a> through Radix's `asChild`, so the app's own
  // link handling applies: consumers intercept internal anchor clicks and route
  // them client-side, and the browser gives cmd/middle-click a new tab plus a
  // status-bar preview. Driving navigation from JS instead (`location.assign`)
  // bypassed all of that and hard-reloaded the whole app on every menu item.
  if (href && !itemIsDisabled) {
    const isExternal = /^[a-z]+:\/\//i.test(href);
    return menuItem(
      <DropdownMenuPrimitive.Item
        {...(dataTestid !== undefined && { "data-testid": dataTestid })}
        className={itemClassName}
        style={headingFontStyle()}
        asChild
        // Close the menu, but never preventDefault — the browser still has to
        // follow the link. Closing explicitly matters more here than for other
        // items: client-side navigation doesn't tear the app down, so a menu
        // left open would hang over the destination page. A hover-opened menu
        // also refuses Radix's own close (see Menu's handleOpenChange).
        onSelect={() => closeMenu()}
      >
        <a
          href={href}
          className="no-underline"
          {...(isExternal
            ? { target: "_blank", rel: "noopener noreferrer" }
            : {})}
        >
          {itemBody}
        </a>
      </DropdownMenuPrimitive.Item>,
    );
  }

  return menuItem(
    <DropdownMenuPrimitive.Item
      disabled={itemIsDisabled}
      {...(dataTestid !== undefined && { "data-testid": dataTestid })}
      className={itemClassName}
      style={headingFontStyle()}
      onSelect={handleSelect}
    >
      {itemBody}
    </DropdownMenuPrimitive.Item>,
  );
}
